import { useCallback, useEffect, useState } from 'react';
import { api, fmt, fmtDate, num } from '../api.ts';

type Tab = 'snapshot' | 'sales' | 'companies' | 'production';

interface Snapshot {
  date: string;
  producedToday: number;
  stagesWorkedToday: number;
  prePressItems: number;
  prePressQuantity: number;
  jobsInProduction: number;
  jobsAwaitingDispatch: number;
  quantityAwaitingDispatch: number;
  jobsShortOfBoard: number;
  totalOutstanding: number;
  totalDispatchedValue: number;
}

const rupees = (v: string | number | null | undefined) => `₹${fmt(v)}`;
const today = () => new Date().toISOString().slice(0, 10);
const monthsAgo = (n: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

/** A period label from a 'YYYY-MM-DD' bucket start. */
const periodLabel = (iso: string, groupBy: 'month' | 'year') => {
  const [y, m] = iso.split('-');
  if (groupBy === 'year') return y ?? iso;
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${MONTHS[Number(m) - 1] ?? m} ${y}`;
};

const GST_NOTE = 'All values exclude GST — no tax is stored in the system';

export default function Reports({ view }: { view: Tab }) {
  const [error, setError] = useState('');

  useEffect(() => { setError(''); }, [view]);

  return (
    <div className="panel">
      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}

      {view === 'snapshot' && <SnapshotTab setError={setError} />}
      {view === 'sales' && <SalesTab setError={setError} />}
      {view === 'companies' && <CompaniesTab setError={setError} />}
      {view === 'production' && <ProductionTab setError={setError} />}
    </div>
  );
}

type Props = { setError: (s: string) => void };

function Csv({ href }: { href: string }) {
  // A plain link: same-origin, so the session cookie is sent and the browser
  // handles the download.
  return <a className="btn" href={href} download style={{ textDecoration: 'none' }}>Download CSV</a>;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  );
}

function SnapshotTab({ setError }: Props) {
  const [s, setS] = useState<Snapshot | null>(null);
  const [date, setDate] = useState(today());

  useEffect(() => {
    api.get<Snapshot>(`/api/reports/snapshot?date=${date}`)
      .then(setS)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the snapshot'));
  }, [date, setError]);

  return (
    <>
      <div className="stagebar">
        <label className="hint" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          Day
          <input type="date" className="search" style={{ width: 160 }} value={date}
                 onChange={(e) => setDate(e.target.value)} />
        </label>
        <span className="hint" style={{ alignSelf: 'center', marginLeft: 'auto' }}>{GST_NOTE}</span>
      </div>

      {!s ? <div className="empty">Loading…</div> : (
        <div style={{ padding: 16, display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
          <Stat label="Produced on this day" value={fmt(s.producedToday)}
                hint={`${s.stagesWorkedToday} stage entr${s.stagesWorkedToday === 1 ? 'y' : 'ies'}`} />
          <Stat label="Jobs in production" value={String(s.jobsInProduction)} />
          <Stat label="Waiting in pre-press" value={fmt(s.prePressQuantity)}
                hint={`${s.prePressItems} order line${s.prePressItems === 1 ? '' : 's'}`} />
          <Stat label="Jobs short of board" value={String(s.jobsShortOfBoard)}
                hint="waiting jobs stock can't cover" />
          <Stat label="Ready to dispatch" value={fmt(s.quantityAwaitingDispatch)}
                hint={`${s.jobsAwaitingDispatch} job${s.jobsAwaitingDispatch === 1 ? '' : 's'}`} />
          <Stat label="Shipped to date" value={rupees(s.totalDispatchedValue)} hint="pre-GST" />
          <Stat label="Due to us" value={rupees(s.totalOutstanding)} hint="pre-GST" />
        </div>
      )}
    </>
  );
}

function SalesTab({ setError }: Props) {
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [groupBy, setGroupBy] = useState<'month' | 'year'>('month');
  const [from, setFrom] = useState(monthsAgo(11));
  const [to, setTo] = useState(today());

  const qs = `from=${from}&to=${to}&groupBy=${groupBy}`;

  const load = useCallback(async () => {
    try {
      setRows((await api.get<{ rows: Record<string, string>[] }>(
        `/api/reports/sales-by-period?${qs}`)).rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load sales');
    }
  }, [qs, setError]);

  useEffect(() => { void load(); }, [load]);

  const total = (k: string) => rows.reduce((t, r) => t + num(r[k]), 0);

  return (
    <>
      <div className="stagebar">
        <label className="hint" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          From <input type="date" className="search" style={{ width: 150 }} value={from}
                      onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="hint" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          To <input type="date" className="search" style={{ width: 150 }} value={to}
                    onChange={(e) => setTo(e.target.value)} />
        </label>
        <select className="search" style={{ width: 130 }} value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as 'month' | 'year')}>
          <option value="month">By month</option>
          <option value="year">By year</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="hint">{GST_NOTE}</span>
          <Csv href={`/api/reports/sales-by-period?${qs}&format=csv`} />
        </div>
      </div>

      <div className="hint" style={{ padding: '10px 16px 0' }}>
        <strong>Ordered</strong> is counted on the order date, <strong>dispatched</strong> on the
        date goods left. The two describe different events and are not expected to match.
      </div>

      {rows.length === 0 ? (
        <div className="empty">Nothing in this range.</div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Period</th><th className="num">Orders</th>
                <th className="num">Ordered qty</th><th className="num">Ordered value</th>
                <th className="num">Dispatched qty</th><th className="num">Dispatched value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.period}>
                  <td>{periodLabel(r.period!, groupBy)}</td>
                  <td className="num dim">{fmt(r.order_count)}</td>
                  <td className="num dim">{fmt(r.ordered_quantity)}</td>
                  <td className="num">{rupees(r.ordered_value)}</td>
                  <td className="num dim">{fmt(r.dispatched_quantity)}</td>
                  <td className="num"><strong>{rupees(r.dispatched_value)}</strong></td>
                </tr>
              ))}
              <tr>
                <td><strong>Total</strong></td>
                <td className="num dim">{fmt(total('order_count'))}</td>
                <td className="num dim">{fmt(total('ordered_quantity'))}</td>
                <td className="num"><strong>{rupees(total('ordered_value'))}</strong></td>
                <td className="num dim">{fmt(total('dispatched_quantity'))}</td>
                <td className="num"><strong>{rupees(total('dispatched_value'))}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function CompaniesTab({ setError }: Props) {
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [search, setSearch] = useState('');

  const qs = search.trim() ? `search=${encodeURIComponent(search.trim())}` : '';

  const load = useCallback(async () => {
    try {
      setRows((await api.get<{ rows: Record<string, string>[] }>(
        `/api/reports/sales-by-customer${qs ? `?${qs}` : ''}`)).rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the company report');
    }
  }, [qs, setError]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <>
      <div className="stagebar">
        <input className="search" placeholder="Search company"
               value={search} onChange={(e) => setSearch(e.target.value)} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="hint">{GST_NOTE}</span>
          <Csv href={`/api/reports/sales-by-customer?${qs}${qs ? '&' : ''}format=csv`} />
        </div>
      </div>

      <div className="hint" style={{ padding: '10px 16px 0' }}>
        All time. <strong>Due to us</strong> is a balance as of now, not a figure for a
        period — which is why this report is not date-ranged.
      </div>

      {rows.length === 0 ? (
        <div className="empty">No company activity yet.</div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Company</th><th className="num">Orders</th>
                <th className="num">Ordered qty</th><th className="num">Ordered value</th>
                <th className="num">Dispatched qty</th><th className="num">Dispatched value</th>
                <th className="num">Due to us</th><th>First order</th><th>Last order</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.customer_id}>
                  <td>{r.customer_name}</td>
                  <td className="num dim">{fmt(r.order_count)}</td>
                  <td className="num dim">{fmt(r.ordered_quantity)}</td>
                  <td className="num">{rupees(r.ordered_value)}</td>
                  <td className="num dim">{fmt(r.dispatched_quantity)}</td>
                  <td className="num"><strong>{rupees(r.dispatched_value)}</strong></td>
                  <td className="num">
                    {num(r.outstanding) > 0 ? rupees(r.outstanding) : <span className="dim">—</span>}
                  </td>
                  <td className="dim">{fmtDate(r.first_order_date ?? null)}</td>
                  <td className="dim">{fmtDate(r.last_order_date ?? null)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ProductionTab({ setError }: Props) {
  const [by, setBy] = useState<'stage' | 'shift' | 'daily'>('stage');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [from, setFrom] = useState(monthsAgo(1));
  const [to, setTo] = useState(today());

  const endpoint = by === 'stage' ? 'production-by-stage'
    : by === 'shift' ? 'production-by-shift' : 'production-daily';
  const qs = `from=${from}&to=${to}`;

  const load = useCallback(async () => {
    try {
      setRows((await api.get<{ rows: Record<string, string>[] }>(
        `/api/reports/${endpoint}?${qs}`)).rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load production');
    }
  }, [endpoint, qs, setError]);

  useEffect(() => { void load(); }, [load]);

  const total = rows.reduce((t, r) => t + num(r.produced_quantity), 0);

  const columns: [string, string][] = by === 'stage'
    ? [['stage_name', 'Stage'], ['days_worked', 'Days'], ['jobs_touched', 'Jobs'],
       ['entry_count', 'Entries'], ['produced_quantity', 'Produced']]
    : by === 'shift'
      ? [['shift_name', 'Shift'], ['machine_name', 'Machine'], ['days_worked', 'Days'],
         ['entry_count', 'Entries'], ['produced_quantity', 'Produced']]
      : [['entry_date', 'Date'], ['stage_name', 'Stage'], ['shift_name', 'Shift'],
         ['machine_name', 'Machine'], ['jobs_touched', 'Jobs'], ['produced_quantity', 'Produced']];

  return (
    <>
      <div className="stagebar">
        <label className="hint" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          From <input type="date" className="search" style={{ width: 150 }} value={from}
                      onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="hint" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          To <input type="date" className="search" style={{ width: 150 }} value={to}
                    onChange={(e) => setTo(e.target.value)} />
        </label>
        <select className="search" style={{ width: 150 }} value={by}
                onChange={(e) => setBy(e.target.value as 'stage' | 'shift' | 'daily')}>
          <option value="stage">By stage</option>
          <option value="shift">By shift</option>
          <option value="daily">Day by day</option>
        </select>
        <div style={{ marginLeft: 'auto' }}>
          <Csv href={`/api/reports/${endpoint}?${qs}&format=csv`} />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="empty">No production recorded in this range.</div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                {columns.map(([k, label], i) => (
                  <th key={k} className={i >= 1 && k !== 'stage_name' && k !== 'shift_name'
                    && k !== 'machine_name' ? 'num' : ''}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {columns.map(([k]) => {
                    const numeric = k === 'produced_quantity' || k === 'entry_count'
                      || k === 'jobs_touched' || k === 'days_worked';
                    return (
                      <td key={k} className={numeric ? 'num' : 'dim'}>
                        {k === 'entry_date' ? fmtDate(r[k] ?? null)
                          : k === 'produced_quantity' ? <strong>{fmt(r[k])}</strong>
                          : numeric ? fmt(r[k]) : (r[k] ?? '—')}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                <td colSpan={columns.length - 1}><strong>Total produced</strong></td>
                <td className="num"><strong>{fmt(total)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
